import { useEffect, useState } from 'react';

const HOVER_QUERY = '(hover: hover) and (pointer: fine)';

/**
 * Shared responsive breakpoint hook — mirrors the `lg` (1024px) Tailwind
 * breakpoint used everywhere else for the mobile/desktop chart split.
 * SSR-safe: defaults to false (desktop) until mounted.
 */
export function useIsBelowLg() {
  const [isBelowLg, setIsBelowLg] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = () => setIsBelowLg(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isBelowLg;
}

/**
 * Shared Recharts `margin` presets for the mobile/desktop chart split.
 * Mobile trims all sides to make room for the smaller viewport; desktop is
 * the original 20px-on-every-side spacing every preset chart used before
 * the mobile pass.
 */
/**
 * Phone-only breakpoint (< 480px). Deliberately NOT `lg`: 480-1023px is the
 * portrait signage panel (a 1080x1920 wall display renders at ~720 CSS px --
 * see docs/portrait-signage-standard.md), which wants the oversized type.
 * Mirrors the `max-width: 479px` block in index.css.
 * SSR-safe: defaults to false (desktop) until mounted.
 */
export function useIsPhone() {
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 479px)');
    const onChange = () => setIsPhone(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isPhone;
}

/**
 * True when the primary pointer can hover (desktop/laptop mouse or trackpad),
 * false on touch. Drives hover-reveal affordances that have no touch equivalent
 * -- callers fall back to a long-press/tap interaction when this is false.
 * Read eagerly so hover-reveal rows never flash visible on first paint.
 */
export function useHasHover() {
  const [hasHover, setHasHover] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia(HOVER_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(HOVER_QUERY);
    const onChange = () => setHasHover(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return hasHover;
}

export const MOBILE_CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 12 };
export const DESKTOP_CHART_MARGIN = { top: 20, right: 20, left: 20, bottom: 20 };

/**
 * Compute a chart's Recharts `margin` prop from the mobile/desktop split,
 * with two independent overrides used by the horizontally-scrollable
 * dual-plot charts (PriceLine, TerminalPriceLine, StackedBar):
 *
 *   - `right`: replaces the desktop right margin (e.g. `mainChartRightMargin`
 *     for axis-sync gutter alignment). Ignored on mobile, where right is
 *     always 8.
 *   - `needsScroll`: when the caller passes this (boolean, not omitted), the
 *     left margin is driven by scroll state instead of breakpoint — 6px when
 *     the chart scrolls horizontally (the gutter carries the real Y axis),
 *     20px otherwise. Callers that omit `needsScroll` keep the original
 *     breakpoint-only left margin (0 mobile / 20 desktop).
 *   - `isPhone`: below 480px the 20px inline-axis left margin is dropped to 0
 *     even when `needsScroll` is `false`. That 20px is desktop breathing room
 *     beside the Y axis; on a 390px screen it reads as a gap between the card
 *     edge and the plot. The 6px scrolling inset is kept — there the gutter
 *     column, not this margin, carries the axis.
 *
 * @param {object}  opts
 * @param {boolean} opts.isBelowLg
 * @param {boolean} [opts.isPhone]
 * @param {boolean} [opts.needsScroll]
 * @param {number}  [opts.right]
 */
export function getResponsiveChartMargin({ isBelowLg, isPhone, needsScroll, right } = {}) {
  const inlineAxisLeft = isPhone ? 0 : 20;
  return {
    top: isBelowLg ? 8 : 20,
    right: isBelowLg ? 8 : right ?? 20,
    left:
      needsScroll === undefined
        ? isBelowLg
          ? 0
          : 20
        : needsScroll
          ? 6
          : inlineAxisLeft,
    bottom: isBelowLg ? 12 : 20,
  };
}
